class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // A throw anywhere in here aborts the custom element upgrade, which leaves
    // <cart-drawer> in the DOM without renderContents()/open(). product-form.js
    // then hits "renderContents is not a function" inside its own .catch(), so
    // the cart is added server-side and the drawer never opens — with no obvious
    // error pointing back here. Neither binding below is worth that.
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    try {
      this.setHeaderCartIconAccessibility();
    } catch (error) {
      console.error('[cart-drawer] header cart icon setup failed', error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    // Becoming visible comes first and is not allowed to depend on anything
    // below. `.drawer` is visibility: hidden until `.active` lands, so if any
    // of the accessibility / analytics work below threw before this ran, the
    // cart would update server-side while the drawer silently stayed shut.
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');

    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    // Everything from here on is enhancement. A failure must not take the open
    // with it, so each step reports itself and the drawer stays open regardless.
    try {
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    } catch (error) {
      console.error('[cart-drawer] summary accessibility setup failed', error);
    }

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    //
    // The method — not just the element — has to be checked. The element is in
    // the Liquid markup either way, but it only gains dispatchViewEvent() once
    // `cart-drawer-items` is registered, and that registration depends on
    // CartItems (cart.js), which in turn depends on window.StandardEvents being
    // populated by the module import in theme.liquid. When that import fails the
    // element is present but never upgraded, and calling through `?.` on the
    // element still throws "dispatchViewEvent is not a function".
    const drawerItems = this.querySelector('cart-drawer-items');
    if (typeof drawerItems?.dispatchViewEvent === 'function') {
      try {
        drawerItems.dispatchViewEvent();
      } catch (error) {
        console.error('[cart-drawer] cart:view dispatch failed', error);
      }
    } else if (drawerItems) {
      console.error(
        '[cart-drawer] <cart-drawer-items> is present but not upgraded — cart.js/CartItems did not register. Check that window.StandardEvents loaded (theme.liquid module import).',
      );
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = parsedState.sections?.[section.id];
      if (!html) {
        // The add-to-cart response did not carry this section. Leaving the
        // existing markup alone shows a stale drawer; blowing up here would
        // stop the open below, which is worse.
        console.error(`[cart-drawer] section "${section.id}" missing from the add-to-cart response`);
        return;
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    setTimeout(() => {
      // Rebinding the overlay is incidental — the markup it lives in was just
      // replaced. It must not gate the open() call that follows.
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// CartItems comes from cart.js, which derives it from
// window.StandardEvents.createViewEventElement(). If that module import failed,
// cart.js aborts at its own class declaration and CartItems is never bound —
// and the bare `extends CartItems` below then threw a ReferenceError at the top
// level of this file, taking the registration with it silently.
if (typeof CartItems === 'undefined') {
  console.error(
    '[cart-drawer] CartItems is unavailable, so <cart-drawer-items> cannot be registered. cart.js did not finish — check that window.StandardEvents was populated by the module import in theme.liquid.',
  );
} else {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
